import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, PLATFORM_ID, effect, inject, signal } from '@angular/core';

const STORAGE_KEY = 'hai-mkt-theme';

type ThemeMode = 'light' | 'dark';

@Component({
  selector: 'app-theme-toggle',
  template: `
    <button
      type="button"
      class="theme-toggle"
      aria-label="Toggle theme"
      [attr.aria-pressed]="theme() === 'dark'"
      (click)="toggleTheme()"
    >
      <span class="sun" aria-hidden="true">☀</span>
      <span class="moon" aria-hidden="true">☾</span>
      <span class="sr-only">Toggle theme</span>
    </button>
  `,
  styles: [
    `
      .theme-toggle {
        inline-size: 2.4rem;
        block-size: 2.4rem;
        border: 1px solid rgb(203 217 231);
        border-radius: 9999px;
        background: rgb(255 255 255);
        color: rgb(34 62 92);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        position: relative;
        cursor: pointer;
      }

      .sun,
      .moon {
        position: absolute;
        font-size: 1rem;
        transition: transform 220ms ease, opacity 220ms ease;
      }

      .sun {
        opacity: 1;
        transform: rotate(0deg) scale(1);
      }

      .moon {
        opacity: 0;
        transform: rotate(90deg) scale(0.6);
      }

      :host-context(.theme-dark) .theme-toggle {
        border-color: rgb(71 91 116);
        background: rgb(17 27 39);
        color: rgb(198 213 230);
      }

      :host-context(.theme-dark) .sun {
        opacity: 0;
        transform: rotate(-90deg) scale(0.6);
      }

      :host-context(.theme-dark) .moon {
        opacity: 1;
        transform: rotate(0deg) scale(1);
      }

      .sr-only {
        position: absolute;
        inline-size: 1px;
        block-size: 1px;
        margin: -1px;
        border: 0;
        padding: 0;
        white-space: nowrap;
        clip-path: inset(100%);
        clip: rect(0 0 0 0);
        overflow: hidden;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThemeToggleComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  readonly theme = signal<ThemeMode>('light');

  constructor() {
    if (this.isBrowser) {
      const storedTheme = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      const prefersDark =
        typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
      const preferredTheme = storedTheme ?? (prefersDark ? 'dark' : 'light');
      this.theme.set(preferredTheme);
    }

    effect(() => {
      if (!this.isBrowser) {
        return;
      }

      const mode = this.theme();
      document.documentElement.classList.toggle('theme-dark', mode === 'dark');
      window.localStorage.setItem(STORAGE_KEY, mode);
    });
  }

  toggleTheme(): void {
    this.theme.update((current) => (current === 'dark' ? 'light' : 'dark'));
  }
}
